import React, { useEffect } from 'react';

import Button from 'components/Button';
import Buttons from 'components/Buttons';
import CoachingConstants from 'coaching/CoachingConstants';
import CurrentUser from 'auth/CurrentUser';
import EventTracking from 'lib/EventTracking';
import Heading from 'components/Heading';
import Row from 'components/Row';
import Select, { Option } from 'components/Select';
import Text from 'components/Text';
import { TimeslotFrequencyInput } from 'globalTypes';

type Props = {
  onSelectFrequency: (selectedFrequency: TimeslotFrequencyInput) => void;
  selectableFrequencies: TimeslotFrequencyInput[];
  interfaceType: 'buttons' | 'select';
  selectedFrequency?: TimeslotFrequencyInput;
  eventPrefix?: string;
};

function getOptionsForFrequencies(frequencies: TimeslotFrequencyInput[]) {
  const frequencyOptions: Option[] = [];
  frequencies.forEach(freq => {
    const option = CoachingConstants.COACHING_FREQUENCIES.find(
      freqOption => freqOption.value === freq
    );
    if (!option) {
      console.error(`Could not find frequency option for ${freq}`);
    }
    frequencyOptions.push(option!);
  });
  return frequencyOptions;
}

export default function CoachingSessionFrequencySelection({
  onSelectFrequency,
  interfaceType,
  selectedFrequency,
  eventPrefix,
  selectableFrequencies,
}: Props) {
  const currentUser = CurrentUser.getUser();
  const hasMultipleRecurringSessionsFeature = Boolean(
    currentUser && currentUser.hasFeature('add_recurring_sessions.web.dec-21')
  );
  const frequencyOptions = getOptionsForFrequencies(selectableFrequencies);

  function trackKey(key: string) {
    eventPrefix && EventTracking.trackKey(`${eventPrefix}.${key}`);
  }

  useEffect(() => {
    trackKey('view');
  }, []);

  const handlerMap = {
    [TimeslotFrequencyInput.weekly]: () => {
      trackKey('weekly.click');
      onSelectFrequency(TimeslotFrequencyInput.weekly);
    },
    [TimeslotFrequencyInput.everyOtherWeek]: () => {
      trackKey('every_other_week.click');
      onSelectFrequency(TimeslotFrequencyInput.everyOtherWeek);
    },
    [TimeslotFrequencyInput.once]: () => {
      trackKey('once.click');
      onSelectFrequency(TimeslotFrequencyInput.once);
    },
  };

  if (interfaceType === 'buttons') {
    return (
      <>
        <Row size="small">
          {hasMultipleRecurringSessionsFeature ? (
            <>
              <Heading level="5">Frequency</Heading>
              <Text>
                How often would you like to meet with
                <br /> your tutor?
              </Text>
            </>
          ) : (
            <Heading level="5" flush>
              How often would you like to meet with your tutor?
            </Heading>
          )}
        </Row>
        <Buttons axis="y" full>
          {frequencyOptions.map(freq => (
            <Button
              data-test={`coaching-session-freq-button-${freq.value}`}
              key={freq.value}
              onClick={handlerMap[freq.value as TimeslotFrequencyInput]}>
              {freq.label}
            </Button>
          ))}
        </Buttons>
      </>
    );
  }

  return (
    <>
      <Row size="xsmall">
        <Heading level="5" flush>
          {hasMultipleRecurringSessionsFeature ? 'Frequency' : 'Session frequency'}
        </Heading>
      </Row>
      <Select
        options={frequencyOptions}
        onChange={(freq: TimeslotFrequencyInput) => handlerMap[freq]()}
        value={selectedFrequency}
      />
    </>
  );
}
